import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mic,
  Calendar as CalendarIcon,
  Flag,
  FileText,
  Paperclip,
  Clock,
  MapPin,
  ArrowRight,
  User,
  X,
  Edit2,
  Check,
} from 'lucide-react'
import { format } from 'date-fns'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { DateTimePicker, DateTimePickerContent } from '@/components/ui/date-time-picker'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { useTaskAutocomplete } from './hooks/use-task-autocomplete'
import { useTaskNLPExtraction } from './hooks/use-task-nlp-extraction'
import { easeOutQuint, itemPresence, smoothSpring } from './motion-presets'
import { LocationPermissionDialog } from '@/components/ui/location-permission-dialog'
import { useAudioRecorder } from '@/hooks/use-audio-recorder'
import { getBrowserLocation } from '@/lib/geolocation'

const useAddTaskOutsideClick = ({ containerRef, initialExpanded, onCancel, setIsExpanded }) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target
      const isRadixPortal =
        target.closest('[data-radix-portal]') ||
        target.closest('[data-slot="popover-content"]') ||
        target.closest('[data-slot="dropdown-menu-content"]') ||
        target.closest('[role="dialog"]') ||
        target.closest('.rdp') // react-day-picker

      if (!containerRef.current || containerRef.current.contains(target) || isRadixPortal) {
        return
      }
      setIsExpanded(initialExpanded || false)
      if (onCancel) {
        onCancel()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [containerRef, initialExpanded, onCancel, setIsExpanded])
}
const resolveInitialText = (value) => value || ''
const resolveInitialDate = (value) => {
  if (!value) return undefined
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}
const blurInput = (inputRef) => {
  inputRef.current?.blur()
}
const resetInputHeight = (inputRef) => {
  const inputElement = inputRef.current
  if (!inputElement) return
  inputElement.style.height = 'auto'
}
const resizeAndFocusInput = (inputRef) => {
  const inputElement = inputRef.current
  if (!inputElement) return
  inputElement.style.height = 'auto'
  inputElement.style.height = `${inputElement.scrollHeight}px`
  inputElement.focus()
}
const AddTaskSubmitTooltip = () => (
  <TooltipContent>
    <div className="flex items-center gap-2">
      Create
      <Kbd>Enter</Kbd>
    </div>
  </TooltipContent>
)
const AddTaskSubmitButton = ({ isTinyScreen, text, onClick }) => {
  const hasText = text.trim().length > 0
  const handleClick = useCallback(
    (e) => {
      e.stopPropagation()
      onClick()
    },
    [onClick],
  )
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="sm"
          onClick={handleClick}
          className={cn(
            'rounded-md text-xs font-semibold relative overflow-hidden transition-opacity transition-transform duration-200',
            isTinyScreen ? 'px-2.5' : 'px-5',
            hasText
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-4 pointer-events-none absolute right-4',
          )}
        >
          <span className="relative z-10">Add</span>
          <div className="absolute inset-0 bg-white/20 opacity-0 hover:opacity-100 transition-opacity duration-200" />
        </Button>
      </TooltipTrigger>
      <AddTaskSubmitTooltip />
    </Tooltip>
  )
}
const AddTaskAutoFillGhost = ({ text, completion }) => {
  if (!completion) return null
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 px-0 py-1.5 text-[13px] font-medium leading-snug whitespace-pre-wrap break-all overflow-hidden"
    >
      <span className="opacity-0">{text}</span>
      <span className="text-muted-foreground/70">{completion}</span>
      <Kbd className="ml-1 scale-75 origin-left inline-flex align-baseline">Tab</Kbd>
    </div>
  )
}
const AddTaskMobileAutoFillButton = ({ isVisible, onAccept }) => (
  <AnimatePresence mode="popLayout" initial={false}>
    {isVisible && (
      <motion.div
        initial={{
          opacity: 0,
          y: 4,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          y: 4,
        }}
        transition={{
          duration: 0.16,
          ease: easeOutQuint,
        }}
        className="absolute right-1.5 bottom-1.5 z-20"
      >
        <Button
          type="button"
          size="icon"
          variant="secondary"
          onClick={onAccept}
          className="h-7 w-7 rounded-full shadow-sm border border-border/20"
          aria-label="Accept suggested text"
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </motion.div>
    )}
  </AnimatePresence>
)
const AddTaskTopRow = ({
  isTinyScreen,
  isExpanded,
  isMobile,
  status,
  text,
  autoFillCompletion,
  isAutoFillLoading,
  isNLPLoading,
  isRecording,
  isTranscribing,
  interimText,
  showMobileAutoFillButton,
  resolvedInputRef,
  handleTextChange,
  handleTextFocus,
  handleTextKeyDown,
  handleAcceptAutoFill,
  handleSubmit,
  onToggleRecording,
}) => (
  <div
    className={cn(
      'flex items-start gap-2 min-h-9 h-auto',
      isTinyScreen ? 'px-2 gap-1.5' : 'px-3 gap-2',
    )}
  >
    {!isTinyScreen && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onToggleRecording()
        }}
        className={cn(
          'flex items-center justify-center h-6 w-6 rounded-full transition-all mt-1.5 relative',
          isExpanded ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          isRecording && 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.5)] scale-110',
          isTranscribing && 'bg-amber-500 text-white',
        )}
      >
        <Mic className={cn('h-3.5 w-3.5', (isNLPLoading || isTranscribing) && 'opacity-20')} />
        {(isNLPLoading || isTranscribing) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={cn(
                'h-3 w-3 border-2 rounded-full animate-spin',
                isTranscribing
                  ? 'border-white border-t-transparent'
                  : 'border-primary border-t-transparent',
              )}
            />
          </div>
        )}
      </button>
    )}
    <div className="flex-1 min-w-0 mt-1">
      <div className="relative">
        <AddTaskAutoFillGhost text={text} completion={autoFillCompletion} />
        {isRecording && interimText && (
          <div className="absolute inset-0 z-20 bg-background/90 backdrop-blur-[2px] px-0 py-1.5 text-[13px] font-medium leading-snug animate-in fade-in zoom-in-95 duration-300">
            <span className="text-primary/90 italic transition-all duration-300">
              “{interimText}
              <span className="animate-pulse">...</span>”
            </span>
          </div>
        )}
        <Textarea
          ref={resolvedInputRef}
          value={text}
          onChange={handleTextChange}
          onFocus={handleTextFocus}
          onKeyDown={handleTextKeyDown}
          className={cn(
            'relative z-10 border-none shadow-none focus-visible:ring-0 px-0 min-h-[28px] py-1.5 resize-none bg-transparent text-[13px] font-medium placeholder:text-muted-foreground/60 overflow-hidden leading-snug break-all transition-opacity',
            showMobileAutoFillButton && 'pr-9',
            isRecording && interimText && 'opacity-0',
          )}
          placeholder={isRecording ? 'Listening...' : isMobile ? 'Add...' : `Add ${status}...`}
        />
        <AddTaskMobileAutoFillButton
          isVisible={showMobileAutoFillButton}
          onAccept={handleAcceptAutoFill}
        />
      </div>
    </div>
    {!isExpanded && (
      <span className="text-[9px] font-bold tracking-[0.2em] text-muted-foreground/70 border border-border/20 rounded-md px-1.5 py-0.5 uppercase">
        {status.charAt(0)}
      </span>
    )}
    <AddTaskSubmitButton isTinyScreen={isTinyScreen} text={text} onClick={handleSubmit} />
  </div>
)
const StartDateOption = ({ isTinyScreen, startDate, stopPropagation, setStartDate }) => {
  const handleDateSelect = useCallback(
    (newDate) => {
      if (newDate) {
        const currentDate = startDate || new Date()
        newDate.setHours(currentDate.getHours(), currentDate.getMinutes())
        setStartDate(newDate)
      } else {
        setStartDate(undefined)
      }
    },
    [startDate, setStartDate],
  )

  const handleTimeChange = useCallback(
    (e) => {
      const timeStr = e.target.value
      if (!startDate) {
        // If no date selected but time changed, assume today
        const now = new Date()
        const [hours, minutes] = timeStr.split(':').map(Number)
        now.setHours(hours, minutes)
        setStartDate(now)
        return
      }

      const [hours, minutes] = timeStr.split(':').map(Number)
      const newDate = new Date(startDate.getTime())
      newDate.setHours(hours, minutes)
      setStartDate(newDate)
    },
    [startDate, setStartDate],
  )

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Badge
          id="add-date"
          variant="secondary"
          className={cn(
            'cursor-pointer shrink-0',
            isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
            startDate && 'bg-primary/15 text-primary',
          )}
          onPointerDown={stopPropagation}
        >
          <CalendarIcon className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {startDate && !isNaN(startDate.getTime()) ? format(startDate, 'MMM d') : 'Start'}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onPointerDown={stopPropagation}
        onClick={stopPropagation}
      >
        <DateTimePickerContent
          selectedDate={startDate}
          onDateSelect={handleDateSelect}
          onTimeChange={handleTimeChange}
        />
      </PopoverContent>
    </Popover>
  )
}
const PriorityOption = ({ isTinyScreen, priority, stopPropagation, handlePrioritySelect }) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Badge
        variant="secondary"
        className={cn(
          'cursor-pointer shrink-0',
          isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
          priority !== 'medium' && 'bg-primary/15 text-primary',
        )}
        onPointerDown={stopPropagation}
      >
        <Flag className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    </DropdownMenuTrigger>
    <DropdownMenuContent onPointerDown={stopPropagation}>
      <DropdownMenuItem data-priority="low" onClick={handlePrioritySelect}>
        <Flag className="mr-2 h-4 w-4 fill-muted-foreground/40 text-muted-foreground" />
        Low
      </DropdownMenuItem>
      <DropdownMenuItem data-priority="medium" onClick={handlePrioritySelect}>
        <Flag className="mr-2 h-4 w-4 fill-primary/60 text-primary" />
        Medium
      </DropdownMenuItem>
      <DropdownMenuItem data-priority="high" onClick={handlePrioritySelect}>
        <Flag className="mr-2 h-4 w-4 fill-destructive/70 text-destructive" />
        High
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)
const DescriptionOption = ({
  isTinyScreen,
  description,
  stopPropagation,
  handleDescriptionChange,
}) => (
  <Popover modal={false}>
    <PopoverTrigger asChild>
      <Badge
        variant="secondary"
        className={cn(
          'cursor-pointer shrink-0',
          isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
          description && 'bg-primary/15 text-primary',
        )}
        onPointerDown={stopPropagation}
      >
        <FileText className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        Description
      </Badge>
    </PopoverTrigger>
    <PopoverContent
      className="w-80 p-3"
      align="start"
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <Textarea
        value={description}
        onChange={handleDescriptionChange}
        placeholder="Add description..."
        className="min-h-[100px] resize-none"
      />
    </PopoverContent>
  </Popover>
)
const AttachOption = ({ isTinyScreen, attachments, handleAttachClick }) => (
  <Badge
    variant="secondary"
    className={cn(
      'cursor-pointer shrink-0',
      isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
      attachments.length > 0 && 'bg-primary/15 text-primary',
    )}
    onClick={handleAttachClick}
  >
    <Paperclip className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
    {attachments.length > 0 ? `${attachments.length} Attached` : 'Attach'}
  </Badge>
)
const RemindOption = ({ isTinyScreen, reminders, stopPropagation, onRemindersChange }) => {
  const firstReminder = reminders[0]
  const count = reminders.length

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Badge
          variant="secondary"
          className={cn(
            'cursor-pointer shrink-0',
            isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
            count > 0 && 'bg-primary/15 text-primary',
          )}
          onPointerDown={stopPropagation}
        >
          <Clock className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {count > 0 ? (
            <span className="flex items-center gap-1">
              {count === 1
                ? firstReminder instanceof Date && !isNaN(firstReminder.getTime())
                  ? format(firstReminder, 'MMM d, h:mm a')
                  : 'Remind'
                : `${count} Reminders`}
            </span>
          ) : (
            'Remind'
          )}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onPointerDown={stopPropagation}
        onClick={stopPropagation}
      >
        <DateTimePicker
          date={firstReminder}
          setDate={(date) => {
            if (date) {
              onRemindersChange([date])
            } else {
              onRemindersChange([])
            }
          }}
          showConfirm={true}
        />
      </PopoverContent>
    </Popover>
  )
}

const AssigneeOption = ({ isTinyScreen, assignees, stopPropagation, setAssignees }) => (
  <Popover modal={false}>
    <PopoverTrigger asChild>
      <Badge
        variant="secondary"
        className={cn(
          'cursor-pointer shrink-0',
          isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
          assignees.length > 0 && 'bg-primary/15 text-primary',
        )}
        onPointerDown={stopPropagation}
      >
        <User className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        {assignees.length > 0 ? assignees.join(', ') : 'Assign'}
      </Badge>
    </PopoverTrigger>
    <PopoverContent
      className="w-64 p-3"
      align="start"
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">
          Assignees
        </p>
        <Input
          placeholder="Type name..."
          className="h-8 text-xs"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value.trim()) {
              setAssignees([...assignees, e.target.value.trim()])
              e.target.value = ''
            }
          }}
        />
        <div className="flex flex-wrap gap-1 mt-2">
          {assignees.map((name, i) => (
            <Badge key={i} variant="outline" className="text-[10px] h-5 px-1.5">
              {name}
              <button
                className="ml-1 hover:text-destructive"
                onClick={() => setAssignees(assignees.filter((_, idx) => idx !== i))}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </PopoverContent>
  </Popover>
)

const LocationSearchInput = ({
  val,
  isLoading,
  isEditing,
  handleValueChange,
  handleInputKeyDown,
  handleAddLocation,
  onCancelEdit,
}) => (
  <div className="flex gap-2">
    <div className="relative flex-1">
      <Input
        value={val}
        onChange={handleValueChange}
        placeholder="Where?"
        className="h-8 text-xs pr-8"
        onKeyDown={handleInputKeyDown}
        autoFocus={isEditing}
      />
      {isLoading && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <div className="h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
    <div className="flex gap-1">
      <Button size="sm" className="h-8 px-3 text-[10px]" onClick={handleAddLocation}>
        {isEditing ? 'Save' : 'Add'}
      </Button>
      {isEditing && (
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onCancelEdit}>
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  </div>
)

const LocationSuggestions = ({ suggestions, handleSuggestionClick }) => (
  <div className="absolute top-full left-0 right-0 mt-1 z-[60] bg-popover border border-border/40 rounded-md shadow-lg overflow-hidden max-h-40 overflow-y-auto">
    {suggestions.map((suggestion, idx) => (
      <button
        key={`${suggestion.name}-${suggestion.lat}-${suggestion.lng}`}
        data-index={idx}
        className="w-full text-left px-2 py-1.5 text-[10px] hover:bg-accent transition-colors border-b last:border-0 border-border/10"
        onClick={handleSuggestionClick}
      >
        <p className="font-medium truncate">{suggestion.name}</p>
      </button>
    ))}
  </div>
)

const LocationBadges = ({ locations, onRemove, onEdit }) => (
  <div className="flex flex-col gap-1.5 mt-2 max-w-full">
    {locations.map((loc) => (
      <Badge
        key={loc.id || loc.name}
        variant="outline"
        className="text-[10px] h-auto min-h-8 py-1.5 px-2.5 flex items-start gap-2 w-full whitespace-normal text-left group/loc-badge border-border/20 hover:border-border transition-colors relative"
      >
        <MapPin className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
        <span className="flex-1 min-w-0 font-medium leading-relaxed">{loc.name}</span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover/loc-badge:opacity-100 transition-opacity shrink-0 ml-1">
          <button
            type="button"
            className="p-1 hover:bg-accent rounded-md text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onEdit(loc)
            }}
          >
            <Edit2 className="h-3 w-3" />
          </button>
          <button
            type="button"
            className="p-1 hover:bg-accent rounded-md text-muted-foreground hover:text-destructive transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(loc.id)
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </Badge>
    ))}
  </div>
)

const LocationPopoverContent = ({
  val,
  isLoading,
  isEditing,
  handleValueChange,
  handleInputKeyDown,
  handleAddLocation,
  suggestions,
  handleSuggestionClick,
  locations,
  onRemove,
  onEdit,
  onCancelEdit,
}) => (
  <div className="space-y-2">
    <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">
      Locations
    </p>
    <div className="flex flex-col gap-2 relative">
      <LocationSearchInput
        val={val}
        isLoading={isLoading}
        isEditing={isEditing}
        handleValueChange={handleValueChange}
        handleInputKeyDown={handleInputKeyDown}
        handleAddLocation={handleAddLocation}
        onCancelEdit={onCancelEdit}
      />

      {suggestions.length > 0 && (
        <LocationSuggestions
          suggestions={suggestions}
          handleSuggestionClick={handleSuggestionClick}
        />
      )}
    </div>
    {locations.length > 0 && (
      <LocationBadges locations={locations} onRemove={onRemove} onEdit={onEdit} />
    )}
  </div>
)

const LocationOption = ({
  isTinyScreen,
  locations,
  stopPropagation,
  onAddLocation,
  onRemoveLocation,
  onUpdateLocation,
}) => {
  const [val, setVal] = useState('')
  const [editingLocId, setEditingLocId] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const debounceTimerRef = useRef(null)
  const handleValueChange = useCallback((e) => {
    const value = e.target.value
    setVal(value)

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)

    if (value.trim().length > 2) {
      debounceTimerRef.current = setTimeout(async () => {
        setIsLoading(true)
        try {
          // Get current location from browser if possible for proximity bias
          let bias = ''
          if (navigator.geolocation) {
            try {
              const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { timeout: 2000 }),
              )
              if (pos) {
                bias = `&lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
              }
            } catch (e) {
              /* ignore */
            }
          }

          const response = await fetch(
            buildBackendUrl(`/api/geo/search?q=${encodeURIComponent(value)}${bias}`),
          )
          const data = await response.json()

          // Geoapify features structure
          const mapped = (data.features || []).map((item) => ({
            name: item.properties.formatted,
            lat: item.geometry.coordinates[1],
            lng: item.geometry.coordinates[0],
          }))
          const unique = Array.from(new Map(mapped.map((m) => [m.name, m])).values())
          setSuggestions(unique)
        } catch (error) {
          console.error('Failed to fetch locations', error)
        } finally {
          setIsLoading(false)
        }
      }, 500)
    } else {
      setSuggestions([])
    }
  }, [])
  const handleAddLocation = useCallback(() => {
    const trimmed = val.trim()
    if (!trimmed) return

    if (editingLocId) {
      onUpdateLocation(editingLocId, { name: trimmed })
      setEditingLocId(null)
    } else {
      onAddLocation({ name: trimmed })
    }
    setVal('')
    setSuggestions([])
  }, [onAddLocation, onUpdateLocation, val, editingLocId])

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      const data = {
        name: suggestion.name,
        lat: suggestion.lat,
        lng: suggestion.lng,
      }
      if (editingLocId) {
        onUpdateLocation(editingLocId, data)
        setEditingLocId(null)
      } else {
        onAddLocation(data)
      }
      setVal('')
      setSuggestions([])
    },
    [onAddLocation, onUpdateLocation, editingLocId],
  )

  const handleEdit = useCallback((loc) => {
    setVal(loc.name)
    setEditingLocId(loc.id)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingLocId(null)
    setVal('')
    setSuggestions([])
  }, [])

  const handleSuggestionClick = useCallback(
    (event) => {
      const { index } = event.currentTarget.dataset
      const suggestionIndex = Number(index)
      const suggestion = suggestions[suggestionIndex]
      if (!suggestion) return
      handleSelectSuggestion(suggestion)
    },
    [handleSelectSuggestion, suggestions],
  )
  const handleInputKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleAddLocation()
      } else if (e.key === 'Escape' && editingLocId) {
        handleCancelEdit()
      }
    },
    [handleAddLocation, editingLocId, handleCancelEdit],
  )
  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Badge
          variant="secondary"
          className={cn(
            'cursor-pointer shrink-0',
            isTinyScreen ? 'h-5 px-1.5 gap-1 text-[9px]' : 'h-6 px-2.5 gap-1.5 text-[10.5px]',
            locations.length > 0 && 'bg-primary/15 text-primary',
          )}
          onPointerDown={stopPropagation}
        >
          <MapPin className={isTinyScreen ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
          {locations.length > 0 ? `${locations.length} Locs` : 'Location'}
        </Badge>
      </PopoverTrigger>{' '}
      <PopoverContent
        className="w-64 p-3"
        align="start"
        onPointerDown={stopPropagation}
        onClick={stopPropagation}
      >
        <LocationPopoverContent
          val={val}
          isLoading={isLoading}
          isEditing={!!editingLocId}
          handleValueChange={handleValueChange}
          handleInputKeyDown={handleInputKeyDown}
          handleAddLocation={handleAddLocation}
          suggestions={suggestions}
          handleSuggestionClick={handleSuggestionClick}
          locations={locations}
          onRemove={onRemoveLocation}
          onEdit={handleEdit}
          onCancelEdit={handleCancelEdit}
        />
      </PopoverContent>
    </Popover>
  )
}
const AddTaskOptions = ({
  isTinyScreen,
  isExpanded,
  startDate,
  setStartDate,
  priority,
  handlePrioritySelect,
  description,
  handleDescriptionChange,
  attachments,
  handleAttachClick,
  reminders,
  setReminders,
  locations,
  onAddLocation,
  onRemoveLocation,
  onUpdateLocation,
  assignees,
  setAssignees,
  stopPropagation,
}) => (
  <AnimatePresence mode="popLayout" initial={false}>
    {isExpanded && (
      <motion.div
        layout="position"
        initial={{
          opacity: 0,
          y: -10,
        }}
        animate={{
          opacity: 1,
          y: 0,
        }}
        exit={{
          opacity: 0,
          y: -10,
        }}
        transition={{
          ...itemPresence.transition,
          layout: smoothSpring,
        }}
        id="add-task-options"
        className={cn(
          'flex flex-nowrap overflow-x-auto no-scrollbar origin-top transform-gpu items-center',
          isTinyScreen ? 'px-2 pb-1 gap-1' : 'px-3 pb-1.5 gap-1.5',
        )}
      >
        <StartDateOption
          isTinyScreen={isTinyScreen}
          startDate={startDate}
          stopPropagation={stopPropagation}
          setStartDate={setStartDate}
        />
        <PriorityOption
          isTinyScreen={isTinyScreen}
          priority={priority}
          stopPropagation={stopPropagation}
          handlePrioritySelect={handlePrioritySelect}
        />
        <AssigneeOption
          isTinyScreen={isTinyScreen}
          assignees={assignees}
          stopPropagation={stopPropagation}
          setAssignees={setAssignees}
        />
        <DescriptionOption
          isTinyScreen={isTinyScreen}
          description={description}
          stopPropagation={stopPropagation}
          handleDescriptionChange={handleDescriptionChange}
        />
        <AttachOption
          isTinyScreen={isTinyScreen}
          attachments={attachments}
          handleAttachClick={handleAttachClick}
        />
        <RemindOption
          isTinyScreen={isTinyScreen}
          reminders={reminders}
          stopPropagation={stopPropagation}
          onRemindersChange={setReminders}
        />
        <LocationOption
          isTinyScreen={isTinyScreen}
          locations={locations}
          stopPropagation={stopPropagation}
          onAddLocation={onAddLocation}
          onRemoveLocation={onRemoveLocation}
          onUpdateLocation={onUpdateLocation}
        />
      </motion.div>
    )}
  </AnimatePresence>
)
const HIGH_PRIORITY_RE =
  /\b(?:p0|sev0|critical|urgent|asap|high\s*p(?:riority)?|highest\s*p(?:riority)?|very\s+important|super\s+important|top\s+priority|must\s+do|blocker)\b/i
const LOW_PRIORITY_RE =
  /\b(?:low\s*p(?:riority)?|p3|nice\s+to\s+have|someday|whenever|not\s+urgent)\b/i
const MEDIUM_PRIORITY_RE = /\b(?:medium\s*p(?:riority)?|normal\s+p(?:riority)?|p1|p2)\b/i

const inferPriorityFromText = (text) => {
  const value = text?.trim()
  if (!value) return null
  if (HIGH_PRIORITY_RE.test(value)) return 'high'
  if (LOW_PRIORITY_RE.test(value)) return 'low'
  if (MEDIUM_PRIORITY_RE.test(value)) return 'medium'
  return null
}

export function AddTask({
  onAdd,
  status,
  disableInteractions,
  inputRef,
  initialExpanded,
  onCancel,
  initialValues,
  onInteract,
}) {
  const [text, setText] = useState('')
  const [description, setDescription] = useState(() =>
    resolveInitialText(initialValues?.description),
  )
  const [isExpanded, setIsExpanded] = useState(Boolean(initialExpanded))
  const [startDate, setStartDate] = useState(() => resolveInitialDate(initialValues?.startDate))
  const [endDate, setEndDate] = useState(() => resolveInitialDate(initialValues?.endDate))
  const [recurrence, setRecurrence] = useState(initialValues?.recurrence ?? 'none')
  const [priority, setPriority] = useState(initialValues?.priority ?? 'medium')
  const [reminders, setReminders] = useState(() => {
    if (initialValues?.reminders && Array.isArray(initialValues.reminders)) {
      return initialValues.reminders.map((r) => new Date(r))
    }
    return []
  })
  const [attachments, setAttachments] = useState([])
  const [locations, setLocations] = useState([])
  const [assignees, setAssignees] = useState([])
  const [subtasks, setSubtasks] = useState([])
  const [showLocationDialog, setShowLocationDialog] = useState(false)
  const [locationTriggerWord, setLocationTriggerWord] = useState('')
  const isMobile = useIsMobile()
  const isTinyScreen = isMobile // For now, treat mobile as tiny screen to maintain existing compact logic
  const containerRef = useRef(null)
  const internalInputRef = useRef(null)
  const resolvedInputRef = inputRef ?? internalInputRef
  const fileInputRef = useRef(null)
  const {
    completion: autoFillCompletion,
    isLoading: isAutoFillLoading,
    hasCompletion: hasAutoFillCompletion,
    acceptCompletion,
  } = useTaskAutocomplete(text, !disableInteractions && isExpanded)

  const { extractTaskDetails, isLoading: isNLPLoading } = useTaskNLPExtraction()
  const lastExtractedTextRef = useRef('')
  const manualOverridesRef = useRef(new Set())

  const { isRecording, isTranscribing, interimText, toggleRecording } = useAudioRecorder()

  const handleToggleRecording = useCallback(async () => {
    const transcribedText = await toggleRecording()
    if (transcribedText) {
      setText(transcribedText)
      setIsExpanded(true)
      // Force input resize and focus after text update
      requestAnimationFrame(() => {
        resizeAndFocusInput(resolvedInputRef)
      })
    }
  }, [toggleRecording, resolvedInputRef])

  const handleDescriptionChange = useCallback((e) => {
    setDescription(e.target.value)
  }, [])

  const handlePrioritySelect = useCallback((e) => {
    e.stopPropagation()
    const nextPriority = e.currentTarget.dataset.priority
    if (nextPriority) {
      setPriority(nextPriority)
    }
  }, [])

  const handleAttachClick = useCallback((e) => {
    e.stopPropagation()
    fileInputRef.current?.click()
  }, [])

  const getRandomOffset = useCallback(() => {
    const buffer = new Uint32Array(1)
    crypto.getRandomValues(buffer)
    return (buffer[0] / 0xffffffff - 0.5) * 0.1
  }, [])

  const handleAddLocation = useCallback((locationData) => {
    const name = typeof locationData === 'string' ? locationData : locationData.name
    // If lat/lng are provided as numbers, use them. Otherwise, don't default to NYC.
    const lat =
      locationData.lat !== undefined && Number.isFinite(Number(locationData.lat))
        ? Number(locationData.lat)
        : undefined
    const lng =
      locationData.lng !== undefined && Number.isFinite(Number(locationData.lng))
        ? Number(locationData.lng)
        : undefined

    setLocations((prev) => {
      // Prevent adding the same location name again if it's already there
      const alreadyExists = prev.some((l) => l.name === name)
      if (alreadyExists) return prev

      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          name,
          lat: lat ?? 0, // Fallback to 0 if really not found, but backend should provide it
          lng: lng ?? 0,
        },
      ]
    })
  }, [])

  const handleFileChange = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map((f) => f.name)
      setAttachments((prev) => [...prev, ...newFiles])
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const [hasRequestedLocation, setHasRequestedLocation] = useState(false)

  const handleAcceptLocation = useCallback(async () => {
    setShowLocationDialog(false)
    setHasRequestedLocation(true)
    try {
      const coords = await getBrowserLocation()
      if (coords) {
        const result = await extractTaskDetails(text, {
          lat: coords.latitude,
          lng: coords.longitude,
        })
        if (result && result.locations?.length > 0 && !manualOverridesRef.current.has('location')) {
          result.locations.forEach((loc) => {
            handleAddLocation(loc)
          })
        }
      }
    } catch (error) {
      console.warn('Geolocation request failed or denied:', error)
    }
  }, [text, extractTaskDetails, handleAddLocation])

  const handleDeclineLocation = useCallback(() => {
    setShowLocationDialog(false)
    setHasRequestedLocation(true) // Don't ask again this session
    toast.info('Location features disabled', {
      description: 'You can still add locations manually using the location badge.',
    })
  }, [])

  useEffect(() => {
    if (!isExpanded || text.trim().length < 5 || text === lastExtractedTextRef.current) return

    const timer = setTimeout(async () => {
      let currentCoords = null

      const result = await extractTaskDetails(text, currentCoords)
      if (result) {
        lastExtractedTextRef.current = text

        // Handle location requirement
        if (result.requiresLocation && !hasRequestedLocation) {
          // Check browser permission status first
          if (typeof navigator !== 'undefined' && navigator.permissions) {
            try {
              const status = await navigator.permissions.query({ name: 'geolocation' })
              if (status.state === 'granted') {
                // Already have permission, just get location
                handleAcceptLocation()
                return
              } else if (status.state === 'denied') {
                // User explicitly blocked it, don't nag with a custom dialog
                setHasRequestedLocation(true)
                return
              }
            } catch (e) {
              console.warn('Permissions API not supported for geolocation', e)
            }
          }

          // Find which word triggered it
          const triggers = ['nearest', 'near', 'around', 'local', 'nearby', 'closest']
          const words = text.toLowerCase().split(/\s+/)
          const found = words.find((w) => triggers.includes(w))
          setLocationTriggerWord(found || 'nearest')
          setShowLocationDialog(true)
          return // Wait for user decision
        }

        if (result.startDate && !manualOverridesRef.current.has('startDate')) {
          const d = new Date(result.startDate)
          if (!isNaN(d.getTime())) setStartDate(d)
        }
        if (result.endDate && !manualOverridesRef.current.has('endDate')) {
          const d = new Date(result.endDate)
          if (!isNaN(d.getTime())) setEndDate(d)
        }
        if (result.recurrence && !manualOverridesRef.current.has('recurrence')) {
          setRecurrence(result.recurrence)
        }
        if (result.priority && !manualOverridesRef.current.has('priority')) {
          setPriority(result.priority)
        }

        // Handle plural locations (initial ones)
        if (result.locations?.length > 0 && !manualOverridesRef.current.has('location')) {
          result.locations.forEach((loc) => {
            // Only auto-add if it has valid coordinates (enriched)
            if (
              Number.isFinite(loc.lat) &&
              Number.isFinite(loc.lng) &&
              loc.lat !== 0 &&
              loc.lng !== 0
            ) {
              handleAddLocation(loc)
            }
          })
        }

        if (!manualOverridesRef.current.has('reminders')) {
          if (result.reminders?.length > 0) {
            const validReminders = result.reminders
              .map((r) => new Date(r))
              .filter((d) => !isNaN(d.getTime()))
            if (validReminders.length > 0) {
              setReminders(validReminders)
            }
          } else {
            // AI explicitly says no reminders, so we should clear them if not manually overridden
            setReminders([])
          }
        }
        if (result.description && !description && !manualOverridesRef.current.has('description')) {
          setDescription(result.description)
        }
        if (
          result.assignee &&
          assignees.length === 0 &&
          !manualOverridesRef.current.has('assignees')
        ) {
          setAssignees([result.assignee])
        }

        if (result.subtasks?.length > 0 && !manualOverridesRef.current.has('subtasks')) {
          setSubtasks(result.subtasks)
        }
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [
    text,
    isExpanded,
    extractTaskDetails,
    handleAddLocation,
    description,
    assignees,
    hasRequestedLocation,
  ])

  useEffect(() => {
    if (manualOverridesRef.current.has('priority')) {
      return
    }

    const inferredPriority = inferPriorityFromText(text)
    if (inferredPriority) {
      setPriority(inferredPriority)
    }
  }, [text])

  useAddTaskOutsideClick({
    containerRef,
    initialExpanded,
    onCancel,
    setIsExpanded,
  })
  const resetForm = useCallback(() => {
    setText('')
    setDescription(resolveInitialText(initialValues?.description))
    setStartDate(resolveInitialDate(initialValues?.startDate))
    setEndDate(resolveInitialDate(initialValues?.endDate))
    setRecurrence(initialValues?.recurrence ?? 'none')
    setPriority(initialValues?.priority ?? 'medium')
    setReminders(() => {
      if (initialValues?.reminders && Array.isArray(initialValues.reminders)) {
        return initialValues.reminders.map((r) => new Date(r))
      }
      return []
    })
    setAttachments([])
    setLocations([])
    setAssignees([])
    setSubtasks([])
    manualOverridesRef.current.clear()
    lastExtractedTextRef.current = ''
    setHasRequestedLocation(false)
    resetInputHeight(resolvedInputRef)
  }, [initialValues, resolvedInputRef])

  useEffect(() => {
    if (initialExpanded) {
      requestAnimationFrame(() => {
        resizeAndFocusInput(resolvedInputRef)
      })
    }
  }, [initialExpanded, resolvedInputRef])

  const handleStartDateChange = useCallback((date) => {
    manualOverridesRef.current.add('startDate')
    setStartDate(date)
  }, [])

  const handlePriorityChange = useCallback(
    (e) => {
      manualOverridesRef.current.add('priority')
      handlePrioritySelect(e)
    },
    [handlePrioritySelect],
  )

  const handleDescriptionManualChange = useCallback(
    (e) => {
      manualOverridesRef.current.add('description')
      handleDescriptionChange(e)
    },
    [handleDescriptionChange],
  )

  const handleReminderManualChange = useCallback((newReminders) => {
    manualOverridesRef.current.add('reminders')
    setReminders(newReminders)
  }, [])

  const handleLocationManualAdd = useCallback(
    (loc) => {
      manualOverridesRef.current.add('location')
      handleAddLocation(loc)
    },
    [handleAddLocation],
  )

  const handleLocationManualRemove = useCallback((id) => {
    manualOverridesRef.current.add('location')
    setLocations((prev) => prev.filter((l) => l.id !== id))
  }, [])

  const handleLocationManualUpdate = useCallback((id, data) => {
    manualOverridesRef.current.add('location')
    setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, ...data } : l)))
  }, [])

  const handleAssigneesManualChange = useCallback((val) => {
    manualOverridesRef.current.add('assignees')
    setAssignees(val)
  }, [])

  const handleAcceptAutoFill = useCallback(() => {
    if (!hasAutoFillCompletion) return

    setText((currentText) => acceptCompletion(currentText))

    requestAnimationFrame(() => {
      resizeAndFocusInput(resolvedInputRef)
    })
  }, [acceptCompletion, hasAutoFillCompletion, resolvedInputRef])

  const handleSubmit = useCallback(async () => {
    const trimmedText = text.trim()
    if (!trimmedText) return false

    // If NLP is still loading or hasn't processed current text, try a quick last-minute extraction
    let finalPayload = {
      priority,
      startDate,
      endDate,
      recurrence,
      description,
      reminders,
      locations,
      assignees,
      subtasks,
    }

    if (trimmedText !== lastExtractedTextRef.current && trimmedText.length >= 5) {
      // Trigger a direct extraction without debounce to ensure we don't miss anything on submit
      const result = await extractTaskDetails(trimmedText)
      if (result) {
        if (result.startDate && !manualOverridesRef.current.has('startDate')) {
          finalPayload.startDate = new Date(result.startDate)
        }
        if (result.priority && !manualOverridesRef.current.has('priority')) {
          finalPayload.priority = result.priority
        }
        if (result.recurrence && !manualOverridesRef.current.has('recurrence')) {
          finalPayload.recurrence = result.recurrence
        }
        if (result.locations?.length > 0 && !manualOverridesRef.current.has('location')) {
          // Filter out locations that don't have valid coordinates to avoid "ocean" pins
          const validEnrichedLocations = result.locations
            .filter(
              (loc) =>
                Number.isFinite(loc.lat) &&
                Number.isFinite(loc.lng) &&
                loc.lat !== 0 &&
                loc.lng !== 0,
            )
            .map((loc) => ({
              id: crypto.randomUUID(),
              name: loc.name,
              lat: loc.lat,
              lng: loc.lng,
              address: loc.address,
            }))

          if (validEnrichedLocations.length > 0) {
            finalPayload.locations = validEnrichedLocations
          }
        }
        if (result.reminders?.length > 0 && !manualOverridesRef.current.has('reminders')) {
          const validReminders = result.reminders
            .map((r) => new Date(r))
            .filter((d) => !isNaN(d.getTime()))
          if (validReminders.length > 0) {
            finalPayload.reminders = validReminders
          }
        }
        if (result.description && !manualOverridesRef.current.has('description')) {
          finalPayload.description = result.description
        }
        if (
          result.assignee &&
          finalPayload.assignees.length === 0 &&
          !manualOverridesRef.current.has('assignees')
        ) {
          finalPayload.assignees = [result.assignee]
        }
        if (result.subtasks?.length > 0 && !manualOverridesRef.current.has('subtasks')) {
          finalPayload.subtasks = result.subtasks
        }

        if (
          result.requiresLocation &&
          !manualOverridesRef.current.has('location') &&
          finalPayload.locations.length === 0
        ) {
          try {
            const coords = await getBrowserLocation()
            if (coords) {
              const enriched = await extractTaskDetails(trimmedText, {
                lat: coords.latitude,
                lng: coords.longitude,
              })
              if (enriched?.locations?.length > 0) {
                const validEnrichedLocations = enriched.locations
                  .filter(
                    (loc) =>
                      Number.isFinite(loc.lat) &&
                      Number.isFinite(loc.lng) &&
                      loc.lat !== 0 &&
                      loc.lng !== 0,
                  )
                  .map((loc) => ({
                    id: crypto.randomUUID(),
                    name: loc.name,
                    lat: loc.lat,
                    lng: loc.lng,
                    address: loc.address,
                  }))
                if (validEnrichedLocations.length > 0) {
                  finalPayload.locations = validEnrichedLocations
                }
              }
            }
          } catch (error) {
            console.warn('Location extraction on submit failed:', error)
          }
        }
      }
    }

    const taskParams = {
      priority: finalPayload.priority,
      has_description: Boolean(finalPayload.description),
      has_start_date: Boolean(finalPayload.startDate),
      has_end_date: Boolean(finalPayload.endDate),
      reminder_count: finalPayload.reminders.length,
      attachment_count: attachments.length,
      location_count: finalPayload.locations.length,
      assignee_count: finalPayload.assignees.length,
    }

    window.record('task_created', taskParams)

    onAdd(trimmedText, {
      ...initialValues,
      priority: finalPayload.priority,
      startDate: finalPayload.startDate?.toISOString(),
      endDate: finalPayload.endDate?.toISOString(),
      recurrence: finalPayload.recurrence || 'none',
      description: finalPayload.description?.trim() || undefined,
      reminders:
        finalPayload.reminders.length > 0
          ? finalPayload.reminders.map((d) => d.toISOString())
          : undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
      locations: finalPayload.locations.length > 0 ? finalPayload.locations : undefined,
      assignees: finalPayload.assignees.length > 0 ? finalPayload.assignees : undefined,
      subtasks: finalPayload.subtasks?.length > 0 ? finalPayload.subtasks : undefined,
    })
    resetForm()
    setHasRequestedLocation(false)
    return true
  }, [
    text,
    onAdd,
    initialValues,
    priority,
    startDate,
    endDate,
    recurrence,
    description,
    reminders,
    attachments,
    locations,
    assignees,
    subtasks,
    resetForm,
    extractTaskDetails,
  ])
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleContextMenuCapture = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleComposerInteract = useCallback(() => {
    if (onInteract) {
      onInteract()
    }
  }, [onInteract])
  const handleTextChange = useCallback(
    (e) => {
      const val = e.target.value
      setText(val)
      if (val === '') {
        resetForm()
      }
      e.target.style.height = 'auto'
      e.target.style.height = `${e.target.scrollHeight}px`
    },
    [resetForm],
  )
  const handleTextFocus = useCallback(() => {
    setIsExpanded(true)
  }, [])
  const handleTextKeyDown = useCallback(
    (e) => {
      e.stopPropagation()
      if (
        e.key === 'Tab' &&
        !e.shiftKey &&
        !e.altKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        hasAutoFillCompletion
      ) {
        e.preventDefault()
        handleAcceptAutoFill()
        return
      }

      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault()
        handleSubmit()
      } else if (e.key === 'Escape') {
        if (onCancel) {
          onCancel()
        } else {
          setIsExpanded(false)
          blurInput(resolvedInputRef)
        }
      }
    },
    [handleAcceptAutoFill, handleSubmit, hasAutoFillCompletion, onCancel, resolvedInputRef],
  )
  return (
    <motion.div
      ref={containerRef}
      data-add-task="true"
      layout="position"
      {...itemPresence}
      transition={{
        ...itemPresence.transition,
        layout: {
          ...smoothSpring,
        },
      }}
      onContextMenuCapture={handleContextMenuCapture}
      onPointerDownCapture={handleComposerInteract}
      onFocusCapture={handleComposerInteract}
      className={cn(
        'group flex flex-col gap-1 rounded-lg border bg-card text-card-foreground overflow-hidden relative transition-all duration-200',
        isExpanded
          ? 'border-border/40 shadow-lg ring-1 ring-primary/5'
          : 'border-border/10 bg-card/60',
        disableInteractions && 'pointer-events-none',
      )}
    >
      {/* Hidden File Input */}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Hover Border Overlay */}
      {!isExpanded && !disableInteractions && (
        <div className="absolute -inset-px rounded-md border border-primary/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" />
      )}
      <AddTaskTopRow
        isTinyScreen={isTinyScreen}
        isExpanded={isExpanded}
        isMobile={isMobile}
        status={status}
        text={text}
        autoFillCompletion={autoFillCompletion}
        isAutoFillLoading={isAutoFillLoading}
        isNLPLoading={isNLPLoading}
        isRecording={isRecording}
        isTranscribing={isTranscribing}
        interimText={interimText}
        showMobileAutoFillButton={isMobile && hasAutoFillCompletion}
        resolvedInputRef={resolvedInputRef}
        handleTextChange={handleTextChange}
        handleTextFocus={handleTextFocus}
        handleTextKeyDown={handleTextKeyDown}
        handleAcceptAutoFill={handleAcceptAutoFill}
        handleSubmit={handleSubmit}
        onToggleRecording={handleToggleRecording}
      />

      <AddTaskOptions
        isTinyScreen={isTinyScreen}
        isExpanded={isExpanded}
        startDate={startDate}
        setStartDate={handleStartDateChange}
        priority={priority}
        handlePrioritySelect={handlePriorityChange}
        description={description}
        handleDescriptionChange={handleDescriptionManualChange}
        attachments={attachments}
        handleAttachClick={handleAttachClick}
        reminders={reminders}
        setReminders={handleReminderManualChange}
        locations={locations}
        onAddLocation={handleLocationManualAdd}
        onRemoveLocation={handleLocationManualRemove}
        onUpdateLocation={handleLocationManualUpdate}
        assignees={assignees}
        setAssignees={handleAssigneesManualChange}
        stopPropagation={stopPropagation}
      />

      <LocationPermissionDialog
        isOpen={showLocationDialog}
        onOpenChange={setShowLocationDialog}
        onAccept={handleAcceptLocation}
        onDecline={handleDeclineLocation}
        triggerWord={locationTriggerWord}
      />
    </motion.div>
  )
}
